import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Loader } from "@/components/Loader";
import { usePermissions } from "@/lib/permissions";
import type { PermissionCheck } from "@repo/schemas/permissionSchema";

interface PermissionRouteProps {
  children: React.ReactNode;
  permissions?: PermissionCheck;
  fallbackRoute?: string;
  showUnauthorized?: boolean;
}

/**
 * Enhanced route protection component with complex permission logic
 */
export function PermissionRoute({
  children,
  permissions,
  fallbackRoute = "/admin",
  showUnauthorized = false,
}: PermissionRouteProps) {
  const { user, session, loading, initialized } = useAuthStore();
  const { checkPermission } = usePermissions();
  const location = useLocation();

  // Show loading while auth state is being initialized
  if (!initialized || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user || !session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check authorization if permissions are required
  if (permissions) {
    const hasAccess = checkPermission(permissions);

    if (!hasAccess) {
      // Log unauthorized access attempt for security monitoring
      console.warn(
        `Unauthorized access attempt to ${location.pathname} by user ${user.email}. Required permissions:`,
        permissions
      );

      // Show unauthorized page or redirect to fallback
      if (showUnauthorized) {
        return <Navigate to="/unauthorized" replace />;
      }

      return <Navigate to={fallbackRoute} replace />;
    }
  }

  // User is authenticated and authorized, render the protected content
  return <>{children}</>;
}

export default PermissionRoute;
